import React, { FunctionComponent, useState } from "react";
import { IAlbum } from "../../../hooks/albums";
import { RightDetailListItem } from "../../RightDetailListItem";
import { strings } from "../../../localization/LocalizedStrings";
import { useUpdateAlbum } from "../../../hooks/albums/useUpdateAlbum";
import { AlertDialog } from "../../AlertDialog";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns as DateAdapter } from "@mui/x-date-pickers/AdapterDateFns/AdapterDateFns";
import { AttributeDialog } from "../../attributes/AttributeDialog";
import { OriginCountriesDesktop } from "../../attributes/originCountries/OriginCountriesDesktop";
import { IOriginCountry } from "../../../hooks/originCountries";
import { useIsMobile } from "../../../hooks/isMobile";
import { PiperDivider } from "../../PiperDivider";

export const AlbumYearListSection: FunctionComponent<IAlbumYearListSectionProps> = ({
  isPublic,
  album,
  onAttributeUpdated
}) => {
  const isMobile = useIsMobile();
  const [isLoadingOriginalYear, setIsLoadingOriginalYear] = useState(false);
  const [isOriginalYearPickerOpen, setIsOriginalYearPickerOpen] = useState(false);
  const [isLoadingReleaseYear, setIsLoadingReleaseYear] = useState(false);
  const [isReleaseYearPickerOpen, setIsReleaseYearPickerOpen] = useState(false);
  const [isLoadingCountry, setIsLoadingCountry] = useState(false);
  const [isOriginCountriesDesktopOpen, setIsOriginCountriesDesktopOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const { updateAlbum } = useUpdateAlbum();

  const updateOriginalYear = async (newValue: string | null) => {
    setIsLoadingOriginalYear(true);
    const { status, body } = await updateAlbum({ uuid: album.uuid, originalYear: newValue });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    } else {
      toast.success(strings.album_update_success);
      onAttributeUpdated(body);
    }
    setIsLoadingOriginalYear(false);
  };

  const updateReleaseYear = async (newValue: string | null) => {
    setIsLoadingReleaseYear(true);
    const { status, body } = await updateAlbum({ uuid: album.uuid, releaseYear: newValue });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    } else {
      toast.success(strings.album_update_success);
      onAttributeUpdated(body);
    }
    setIsLoadingReleaseYear(false);
  };

  const updateOriginCountry = async (newValue: IOriginCountry | null) => {
    setIsOriginCountriesDesktopOpen(false);
    setIsLoadingCountry(true);
    const { status, body } = await updateAlbum({
      uuid: album.uuid,
      originCountryId: newValue?.uuid ?? null
    });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    } else {
      toast.success(strings.album_update_success);
      onAttributeUpdated(body);
    }
    setIsLoadingCountry(false);
  };

  return (
    <div>
      <LocalizationProvider
        localeText={
          strings.datePickerLocale.components.MuiLocalizationProvider.defaultProps.localeText
        }
        dateAdapter={DateAdapter}
        adapterLocale={strings.fnsLocale}
      >
        <MobileDatePicker
          slotProps={{
            actionBar: {
              actions: ["clear", "cancel"]
            }
          }}
          views={["year"]}
          format={"yyyy"}
          minDate={new Date(strings.minDate)}
          value={album.originalYear ? new Date(`${album.originalYear}-06`) : null}
          closeOnSelect
          onAccept={async date => {
            const originalYear = date == null ? null : (date as unknown as Date).getFullYear();
            await updateOriginalYear(originalYear ? String(originalYear) : null);
          }}
          onClose={() => setIsOriginalYearPickerOpen(false)}
          open={isOriginalYearPickerOpen}
          slots={{
            field: () => (
              <RightDetailListItem
                disabled={isPublic}
                title={strings.original_year_title}
                detail={album.originalYear ?? strings.unknown}
                onClick={() => setIsOriginalYearPickerOpen(true)}
                isLoading={isLoadingOriginalYear}
              />
            )
          }}
        />
      </LocalizationProvider>
      <PiperDivider />
      <LocalizationProvider
        localeText={
          strings.datePickerLocale.components.MuiLocalizationProvider.defaultProps.localeText
        }
        dateAdapter={DateAdapter}
        adapterLocale={strings.fnsLocale}
      >
        <MobileDatePicker
          slotProps={{
            actionBar: {
              actions: ["clear", "cancel"]
            }
          }}
          views={["year"]}
          format={"yyyy"}
          minDate={new Date(strings.minDate)}
          closeOnSelect
          value={album.releaseYear ? new Date(`${album.releaseYear}-06`) : null}
          onAccept={async date => {
            const releaseYear = date == null ? null : (date as unknown as Date).getFullYear();
            await updateReleaseYear(releaseYear ? String(releaseYear) : null);
          }}
          onClose={() => setIsReleaseYearPickerOpen(false)}
          open={isReleaseYearPickerOpen}
          slots={{
            field: () => (
              <RightDetailListItem
                disabled={isPublic}
                title={strings.release_year_title}
                detail={album.releaseYear ?? strings.unknown}
                onClick={() => setIsReleaseYearPickerOpen(true)}
                isLoading={isLoadingReleaseYear}
              />
            )
          }}
        />
      </LocalizationProvider>
      <PiperDivider />
      <RightDetailListItem
        disabled={isPublic}
        title={strings.origin_country_title}
        detail={
          album.originCountry
            ? `${album.originCountry.name} ${album.originCountry.country ? `   ${album.originCountry.country.emoji}` : ""}`
            : strings.unknown
        }
        isLoading={isLoadingCountry}
        onClick={() => setIsOriginCountriesDesktopOpen(true)}
      />
      <AttributeDialog
        open={isOriginCountriesDesktopOpen}
        onClose={() => setIsOriginCountriesDesktopOpen(false)}
      >
        <OriginCountriesDesktop
          handleClose={() => {
            setIsOriginCountriesDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 6 : 4}
          preselectedId={album.originCountry?.uuid}
          onOriginCountrySelected={updateOriginCountry}
          onClearValue={() => updateOriginCountry(null)}
        />
      </AttributeDialog>
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </div>
  );
};

export interface IAlbumYearListSectionProps {
  isPublic: boolean;
  album: IAlbum;
  onAttributeUpdated: (album: IAlbum) => void;
}
